import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Snackbar,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { companiesAPI as companyAPI } from '../services';
import { useAuth } from '../context/AuthContext';
import { isSuperAdmin } from '../utils/userPermissions';

interface Company {
  companyid: string;
  companyname: string;
  companytype: string;
  logo?: string;
  style?: string;
  prefixword?: string;
  timezone: string;
  remark?: string;
  pourcode?: string;
  calibrationcode?: string;
  reportprefix?: string;
  reportno?: string;
  dateformat?: string;
  tempformat?: string;
  evaformat?: string;
  strengthformat?: string;
  densityformat?: string;
  maturitymethod?: string;
  defaultmaturitymethod?: string;
  language?: string;
  defaultcalibrationspecifiedtemp?: number;
  defaultcalibrationinternaltemp?: number;
  status?: string;
  loggercalibrationfrequency?: number;
  productcalibrationfrequency?: number;
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
  hasmass?: boolean;
  hasmaturity?: boolean;
  hasmatchcuring?: boolean;
  hasweather?: boolean;
  haseva?: boolean;
  hasexternalth?: boolean;
  hasextend?: boolean;
  hasb80?: boolean;
  hassteam?: boolean;
  dataapi?: boolean;
  contactname?: string;
  contactemail?: string;
  windspeedformat?: string;
  isactive: boolean;
  createtime?: string;
}

// The six licensed pour functions (mirrors backend monitoring_access.py).
// Field names are the CompanyInfo licensing flags; labels are the same
// function names the create-pour wizard shows, so admins can see exactly
// which wizard card each toggle controls.
type LicenseField =
  | 'hasmass'
  | 'hasmaturity'
  | 'hasmatchcuring'
  | 'hasweather'
  | 'haseva'
  | 'hasexternalth';

const LICENSE_FIELDS: { field: LicenseField; labelKey: string; labelFallback: string }[] = [
  { field: 'hasmass', labelKey: 'companyView.funcTemperatureOnly', labelFallback: 'Temperature Only (Mass Concrete)' },
  { field: 'hasmaturity', labelKey: 'companyView.funcConcreteMaturity', labelFallback: 'Concrete Maturity' },
  { field: 'hasmatchcuring', labelKey: 'companyView.funcTmc', labelFallback: 'Temperature Match Curing (TMC)' },
  { field: 'hasweather', labelKey: 'companyView.funcWeather', labelFallback: 'Onsite Weather Station' },
  { field: 'haseva', labelKey: 'companyView.funcEva', labelFallback: 'Evaporation Rate Calculations' },
  { field: 'hasexternalth', labelKey: 'companyView.funcExternalTH', labelFallback: 'External Temperature & Humidity' },
];

// A function is auto-included (granted implicitly) when any function that
// depends on it is licensed: Maturity/TMC/Evaporation need Temperature
// Only, TMC needs Maturity. Mirrors dependency_closure() server-side.
const LICENSE_REQUIRED_BY: Record<LicenseField, LicenseField[]> = {
  hasmass: ['hasmaturity', 'hasmatchcuring', 'haseva'],
  hasmaturity: ['hasmatchcuring'],
  hasmatchcuring: [],
  hasweather: [],
  haseva: [],
  hasexternalth: [],
};

/** Explicit values for all six licensing flags, resolving NULLs the same
 *  way the backend does: all-NULL = never configured = full access;
 *  otherwise NULL means off, except External T&H where NULL means on
 *  (the flag postdates the others). Returning explicit booleans means the
 *  next save materializes the state the admin actually sees. */
const normalizeLicensing = (
  data: Partial<Company>,
): { values: Record<LicenseField, boolean>; wasUnconfigured: boolean } => {
  const raw = LICENSE_FIELDS.map(({ field }) => data[field]);
  const wasUnconfigured = raw.every((v) => v === null || v === undefined);
  const values = {} as Record<LicenseField, boolean>;
  for (const { field } of LICENSE_FIELDS) {
    const v = data[field];
    if (wasUnconfigured) {
      values[field] = true;
    } else if (field === 'hasexternalth') {
      values[field] = v !== false;
    } else {
      values[field] = !!v;
    }
  }
  return { values, wasUnconfigured };
};

const CompanyEdit: React.FC = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const userIsSuperAdmin = isSuperAdmin(user);
  // Create mode: mounted on /companies/new with no :companyId param
  const isNew = !companyId;

  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Form data state. New companies start with all pour functions licensed
  // (explicit, so the create persists real values instead of NULLs).
  const [formData, setFormData] = useState<Partial<Company>>(
    isNew
      ? {
          companytype: '03',
          timezone: 'AUS Eastern Standard Time',
          isactive: true,
          language: 'en',
          hasmass: true,
          hasmaturity: true,
          hasmatchcuring: true,
          hasweather: true,
          haseva: true,
          hasexternalth: true,
        }
      : {},
  );
  // True when the loaded company had no licensing flags configured at all
  // (legacy full-access default) — shown as a note so the admin knows the
  // all-on toggles reflect effective access, not stored values.
  const [wasUnconfigured, setWasUnconfigured] = useState(false);

  const fetchCompany = useCallback(async () => {
    if (!companyId) return;

    try {
      setLoading(true);
      setError(null);
      const response = await companyAPI.getCompanyById(companyId);
      setCompany(response);
      const licensing = normalizeLicensing(response);
      setWasUnconfigured(licensing.wasUnconfigured);
      setFormData({ ...response, ...licensing.values });
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Failed to load company data');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchCompany();
  }, [fetchCompany]);

  const handleInputChange = (field: keyof Company) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | any
  ) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    if (isNew) {
      if (!formData.companyname?.trim()) {
        setError(t('companyEdit.nameRequired', 'Company name is required'));
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      try {
        setSaving(true);
        setError(null);
        const created = await companyAPI.createCompany({
          companyname: formData.companyname.trim(),
          companytype: formData.companytype || '03',
          address: formData.address,
          phone: formData.phone,
          email: formData.email,
          website: formData.website,
          // Backend field is `contactname` (the CompanyCreate TS type's
          // `contactperson` doesn't exist server-side)
          contactname: formData.contactname,
          timezone: formData.timezone || 'AUS Eastern Standard Time',
          isactive: formData.isactive,
        } as any);
        // The create endpoint only accepts the basics — persist the rest of
        // the form (feature flags, formats, settings) with a follow-up update.
        await companyAPI.updateCompany(created.companyid, formData);
        setSuccess(true);
        setTimeout(() => {
          navigate(`/companies/${created.companyid}`);
        }, 1000);
      } catch (error: any) {
        setError(error.response?.data?.detail || t('companyEdit.createFailed', 'Failed to create company'));
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } finally {
        setSaving(false);
      }
      return;
    }

    if (!companyId) return;

    try {
      setSaving(true);
      setError(null);

      await companyAPI.updateCompany(companyId, formData);
      setSuccess(true);

      // Scroll to top to show success alert
      window.scrollTo({ top: 0, behavior: 'smooth' });

      // Redirect back to view page after successful save
      setTimeout(() => {
        navigate(`/companies/${companyId}`);
      }, 1500);

    } catch (error: any) {
      setError(error.response?.data?.detail || 'Failed to update company');
      // Scroll to top to show error alert
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    if (companyId) {
      navigate(`/companies/${companyId}`);
    } else if (userIsSuperAdmin) {
      navigate('/companies');
    } else {
      navigate(-1);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error && !company && !isNew) {
    return (
      <Box p={3}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack}>
          {t('common.back')}
        </Button>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center">
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ mr: 2 }}
          >
            {t('common.back')}
          </Button>
          <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {isNew
              ? t('companies.addCompany', 'Add Company')
              : formData.companyname || t('common.edit', 'Edit')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving}
        >
          {saving
            ? t('common.saving')
            : isNew
              ? t('common.create', 'Create')
              : t('common.save')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Licensed pour functions — which pour types this client may use */}
        <Grid size={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('companyView.licensedPourFunctions', 'Licensed Pour Functions')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t(
                  'companyView.licensedPourFunctionsHint',
                  'Controls which pour functions users of this client can select when creating a pour. Functions that are switched off appear greyed out in the create-pour wizard.',
                )}
              </Typography>
              {wasUnconfigured && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t(
                    'companyView.unconfiguredLicensingNote',
                    'This client has never had its licensing configured, so it currently has full access to all pour functions (shown below). Saving this page will store the toggles as the client’s licensed set.',
                  )}
                </Alert>
              )}
              <Grid container spacing={2}>
                {LICENSE_FIELDS.map(({ field, labelKey, labelFallback }) => {
                  const explicitlyOn = !!formData[field];
                  const requiredBy = LICENSE_REQUIRED_BY[field].filter(
                    (f) => !!formData[f],
                  );
                  const autoIncluded = !explicitlyOn && requiredBy.length > 0;
                  const requiredByNames = requiredBy
                    .map((f) => {
                      const def = LICENSE_FIELDS.find((o) => o.field === f);
                      return def ? t(def.labelKey, def.labelFallback) : f;
                    })
                    .join(', ');
                  return (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={field}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={explicitlyOn || autoIncluded}
                            disabled={autoIncluded}
                            onChange={handleInputChange(field)}
                          />
                        }
                        label={t(labelKey, labelFallback)}
                      />
                      {autoIncluded && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ pl: 6, mt: -0.5 }}
                        >
                          {t('companyView.autoIncludedRequiredBy', {
                            defaultValue: 'Included automatically, required by {{names}}',
                            names: requiredByNames,
                          })}
                        </Typography>
                      )}
                    </Grid>
                  );
                })}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        {/* Other feature flags — legacy switches unrelated to pour licensing */}
        <Grid size={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('companyView.otherFeatureFlags', 'Other Feature Flags')}
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.hasextend || false}
                        onChange={handleInputChange('hasextend')}
                      />
                    }
                    label={t('companyView.extendedMonitoring')}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.hasb80 || false}
                        onChange={handleInputChange('hasb80')}
                      />
                    }
                    label={t('companyView.b80Specification')}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.hassteam || false}
                        onChange={handleInputChange('hassteam')}
                      />
                    }
                    label={t('companyView.steamCuring')}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.dataapi || false}
                        onChange={handleInputChange('dataapi')}
                      />
                    }
                    label={t('companyView.dataApi')}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isactive || false}
                        onChange={handleInputChange('isactive')}
                      />
                    }
                    label={t('common.active')}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        {/* Basic Information */}
        <Grid size={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('companyView.basicInformation')}
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    label={t('companyView.companyName')}
                    value={formData.companyname || ''}
                    onChange={handleInputChange('companyname')}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <FormControl fullWidth>
                    <InputLabel>{t('companyView.companyType')}</InputLabel>
                    <Select
                      value={formData.companytype || ''}
                      onChange={handleInputChange('companytype')}
                      label={t('companyView.companyType')}
                    >
                      <MenuItem value="02">{t('companies.largeClient')}</MenuItem>
                      <MenuItem value="03">{t('companies.mediumClient')}</MenuItem>
                      <MenuItem value="04">{t('companies.smallClient')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <FormControl fullWidth>
                    <InputLabel>{t('companyView.timezone')}</InputLabel>
                    <Select
                      value={formData.timezone || ''}
                      onChange={handleInputChange('timezone')}
                      label={t('companyView.timezone')}
                    >
                      <MenuItem value="Dateline Standard Time">(UTC-12:00) International Date Line West</MenuItem>
                      <MenuItem value="UTC-11">(UTC-11:00) Coordinated Universal Time-11</MenuItem>
                      <MenuItem value="Aleutian Standard Time">(UTC-10:00) Aleutian Islands</MenuItem>
                      <MenuItem value="Hawaiian Standard Time">(UTC-10:00) Hawaii</MenuItem>
                      <MenuItem value="Marquesas Standard Time">(UTC-09:30) Marquesas Islands</MenuItem>
                      <MenuItem value="Alaskan Standard Time">(UTC-09:00) Alaska</MenuItem>
                      <MenuItem value="UTC-09">(UTC-09:00) Coordinated Universal Time-09</MenuItem>
                      <MenuItem value="Pacific Standard Time (Mexico)">(UTC-08:00) Baja California</MenuItem>
                      <MenuItem value="UTC-08">(UTC-08:00) Coordinated Universal Time-08</MenuItem>
                      <MenuItem value="Pacific Standard Time">(UTC-08:00) Pacific Time (US & Canada)</MenuItem>
                      <MenuItem value="US Mountain Standard Time">(UTC-07:00) Arizona</MenuItem>
                      <MenuItem value="Mountain Standard Time (Mexico)">(UTC-07:00) La Paz, Mazatlan</MenuItem>
                      <MenuItem value="Mountain Standard Time">(UTC-07:00) Mountain Time (US & Canada)</MenuItem>
                      <MenuItem value="Yukon Standard Time">(UTC-07:00) Yukon</MenuItem>
                      <MenuItem value="Central America Standard Time">(UTC-06:00) Central America</MenuItem>
                      <MenuItem value="Central Standard Time">(UTC-06:00) Central Time (US & Canada)</MenuItem>
                      <MenuItem value="Easter Island Standard Time">(UTC-06:00) Easter Island</MenuItem>
                      <MenuItem value="Central Standard Time (Mexico)">(UTC-06:00) Guadalajara, Mexico City, Monterrey</MenuItem>
                      <MenuItem value="Canada Central Standard Time">(UTC-06:00) Saskatchewan</MenuItem>
                      <MenuItem value="SA Pacific Standard Time">(UTC-05:00) Bogota, Lima, Quito, Rio Branco</MenuItem>
                      <MenuItem value="Eastern Standard Time (Mexico)">(UTC-05:00) Chetumal</MenuItem>
                      <MenuItem value="Eastern Standard Time">(UTC-05:00) Eastern Time (US & Canada)</MenuItem>
                      <MenuItem value="Haiti Standard Time">(UTC-05:00) Haiti</MenuItem>
                      <MenuItem value="Cuba Standard Time">(UTC-05:00) Havana</MenuItem>
                      <MenuItem value="US Eastern Standard Time">(UTC-05:00) Indiana (East)</MenuItem>
                      <MenuItem value="Turks And Caicos Standard Time">(UTC-05:00) Turks and Caicos</MenuItem>
                      <MenuItem value="Paraguay Standard Time">(UTC-04:00) Asuncion</MenuItem>
                      <MenuItem value="Atlantic Standard Time">(UTC-04:00) Atlantic Time (Canada)</MenuItem>
                      <MenuItem value="Venezuela Standard Time">(UTC-04:00) Caracas</MenuItem>
                      <MenuItem value="Central Brazilian Standard Time">(UTC-04:00) Cuiaba</MenuItem>
                      <MenuItem value="SA Western Standard Time">(UTC-04:00) Georgetown, La Paz, Manaus, San Juan</MenuItem>
                      <MenuItem value="Pacific SA Standard Time">(UTC-04:00) Santiago</MenuItem>
                      <MenuItem value="Newfoundland Standard Time">(UTC-03:30) Newfoundland</MenuItem>
                      <MenuItem value="Tocantins Standard Time">(UTC-03:00) Araguaina</MenuItem>
                      <MenuItem value="E. South America Standard Time">(UTC-03:00) Brasilia</MenuItem>
                      <MenuItem value="SA Eastern Standard Time">(UTC-03:00) Cayenne, Fortaleza</MenuItem>
                      <MenuItem value="Argentina Standard Time">(UTC-03:00) City of Buenos Aires</MenuItem>
                      <MenuItem value="Montevideo Standard Time">(UTC-03:00) Montevideo</MenuItem>
                      <MenuItem value="Magallanes Standard Time">(UTC-03:00) Punta Arenas</MenuItem>
                      <MenuItem value="Saint Pierre Standard Time">(UTC-03:00) Saint Pierre and Miquelon</MenuItem>
                      <MenuItem value="Bahia Standard Time">(UTC-03:00) Salvador</MenuItem>
                      <MenuItem value="UTC-02">(UTC-02:00) Coordinated Universal Time-02</MenuItem>
                      <MenuItem value="Greenland Standard Time">(UTC-02:00) Greenland</MenuItem>
                      <MenuItem value="Mid-Atlantic Standard Time">(UTC-02:00) Mid-Atlantic - Old</MenuItem>
                      <MenuItem value="Azores Standard Time">(UTC-01:00) Azores</MenuItem>
                      <MenuItem value="Cape Verde Standard Time">(UTC-01:00) Cabo Verde Is.</MenuItem>
                      <MenuItem value="UTC">(UTC) Coordinated Universal Time</MenuItem>
                      <MenuItem value="GMT Standard Time">(UTC+00:00) Dublin, Edinburgh, Lisbon, London</MenuItem>
                      <MenuItem value="Greenwich Standard Time">(UTC+00:00) Monrovia, Reykjavik</MenuItem>
                      <MenuItem value="Sao Tome Standard Time">(UTC+00:00) Sao Tome</MenuItem>
                      <MenuItem value="Morocco Standard Time">(UTC+01:00) Casablanca</MenuItem>
                      <MenuItem value="W. Europe Standard Time">(UTC+01:00) Amsterdam, Berlin, Bern, Rome, Stockholm, Vienna</MenuItem>
                      <MenuItem value="Central Europe Standard Time">(UTC+01:00) Belgrade, Bratislava, Budapest, Ljubljana, Prague</MenuItem>
                      <MenuItem value="Romance Standard Time">(UTC+01:00) Brussels, Copenhagen, Madrid, Paris</MenuItem>
                      <MenuItem value="Central European Standard Time">(UTC+01:00) Sarajevo, Skopje, Warsaw, Zagreb</MenuItem>
                      <MenuItem value="W. Central Africa Standard Time">(UTC+01:00) West Central Africa</MenuItem>
                      <MenuItem value="GTB Standard Time">(UTC+02:00) Athens, Bucharest</MenuItem>
                      <MenuItem value="Middle East Standard Time">(UTC+02:00) Beirut</MenuItem>
                      <MenuItem value="Egypt Standard Time">(UTC+02:00) Cairo</MenuItem>
                      <MenuItem value="E. Europe Standard Time">(UTC+02:00) Chisinau</MenuItem>
                      <MenuItem value="West Bank Standard Time">(UTC+02:00) Gaza, Hebron</MenuItem>
                      <MenuItem value="South Africa Standard Time">(UTC+02:00) Harare, Pretoria</MenuItem>
                      <MenuItem value="FLE Standard Time">(UTC+02:00) Helsinki, Kyiv, Riga, Sofia, Tallinn, Vilnius</MenuItem>
                      <MenuItem value="Israel Standard Time">(UTC+02:00) Jerusalem</MenuItem>
                      <MenuItem value="South Sudan Standard Time">(UTC+02:00) Juba</MenuItem>
                      <MenuItem value="Kaliningrad Standard Time">(UTC+02:00) Kaliningrad</MenuItem>
                      <MenuItem value="Sudan Standard Time">(UTC+02:00) Khartoum</MenuItem>
                      <MenuItem value="Libya Standard Time">(UTC+02:00) Tripoli</MenuItem>
                      <MenuItem value="Namibia Standard Time">(UTC+02:00) Windhoek</MenuItem>
                      <MenuItem value="Jordan Standard Time">(UTC+03:00) Amman</MenuItem>
                      <MenuItem value="Arabic Standard Time">(UTC+03:00) Baghdad</MenuItem>
                      <MenuItem value="Syria Standard Time">(UTC+03:00) Damascus</MenuItem>
                      <MenuItem value="Turkey Standard Time">(UTC+03:00) Istanbul</MenuItem>
                      <MenuItem value="Arab Standard Time">(UTC+03:00) Kuwait, Riyadh</MenuItem>
                      <MenuItem value="Belarus Standard Time">(UTC+03:00) Minsk</MenuItem>
                      <MenuItem value="Russian Standard Time">(UTC+03:00) Moscow, St. Petersburg</MenuItem>
                      <MenuItem value="E. Africa Standard Time">(UTC+03:00) Nairobi</MenuItem>
                      <MenuItem value="Volgograd Standard Time">(UTC+03:00) Volgograd</MenuItem>
                      <MenuItem value="Iran Standard Time">(UTC+03:30) Tehran</MenuItem>
                      <MenuItem value="Arabian Standard Time">(UTC+04:00) Abu Dhabi, Muscat</MenuItem>
                      <MenuItem value="Astrakhan Standard Time">(UTC+04:00) Astrakhan, Ulyanovsk</MenuItem>
                      <MenuItem value="Azerbaijan Standard Time">(UTC+04:00) Baku</MenuItem>
                      <MenuItem value="Russia Time Zone 3">(UTC+04:00) Izhevsk, Samara</MenuItem>
                      <MenuItem value="Mauritius Standard Time">(UTC+04:00) Port Louis</MenuItem>
                      <MenuItem value="Saratov Standard Time">(UTC+04:00) Saratov</MenuItem>
                      <MenuItem value="Georgian Standard Time">(UTC+04:00) Tbilisi</MenuItem>
                      <MenuItem value="Caucasus Standard Time">(UTC+04:00) Yerevan</MenuItem>
                      <MenuItem value="Afghanistan Standard Time">(UTC+04:30) Kabul</MenuItem>
                      <MenuItem value="West Asia Standard Time">(UTC+05:00) Ashgabat, Tashkent</MenuItem>
                      <MenuItem value="Qyzylorda Standard Time">(UTC+05:00) Astana</MenuItem>
                      <MenuItem value="Ekaterinburg Standard Time">(UTC+05:00) Ekaterinburg</MenuItem>
                      <MenuItem value="Pakistan Standard Time">(UTC+05:00) Islamabad, Karachi</MenuItem>
                      <MenuItem value="India Standard Time">(UTC+05:30) Chennai, Kolkata, Mumbai, New Delhi</MenuItem>
                      <MenuItem value="Sri Lanka Standard Time">(UTC+05:30) Sri Jayawardenepura</MenuItem>
                      <MenuItem value="Nepal Standard Time">(UTC+05:45) Kathmandu</MenuItem>
                      <MenuItem value="Central Asia Standard Time">(UTC+06:00) Bishkek</MenuItem>
                      <MenuItem value="Bangladesh Standard Time">(UTC+06:00) Dhaka</MenuItem>
                      <MenuItem value="Omsk Standard Time">(UTC+06:00) Omsk</MenuItem>
                      <MenuItem value="Myanmar Standard Time">(UTC+06:30) Yangon (Rangoon)</MenuItem>
                      <MenuItem value="SE Asia Standard Time">(UTC+07:00) Bangkok, Hanoi, Jakarta</MenuItem>
                      <MenuItem value="Altai Standard Time">(UTC+07:00) Barnaul, Gorno-Altaysk</MenuItem>
                      <MenuItem value="W. Mongolia Standard Time">(UTC+07:00) Hovd</MenuItem>
                      <MenuItem value="North Asia Standard Time">(UTC+07:00) Krasnoyarsk</MenuItem>
                      <MenuItem value="N. Central Asia Standard Time">(UTC+07:00) Novosibirsk</MenuItem>
                      <MenuItem value="Tomsk Standard Time">(UTC+07:00) Tomsk</MenuItem>
                      <MenuItem value="China Standard Time">(UTC+08:00) Beijing, Chongqing, Hong Kong, Urumqi</MenuItem>
                      <MenuItem value="North Asia East Standard Time">(UTC+08:00) Irkutsk</MenuItem>
                      <MenuItem value="Singapore Standard Time">(UTC+08:00) Kuala Lumpur, Singapore</MenuItem>
                      <MenuItem value="W. Australia Standard Time">(UTC+08:00) Perth</MenuItem>
                      <MenuItem value="Taipei Standard Time">(UTC+08:00) Taipei</MenuItem>
                      <MenuItem value="Ulaanbaatar Standard Time">(UTC+08:00) Ulaanbaatar</MenuItem>
                      <MenuItem value="Aus Central W. Standard Time">(UTC+08:45) Eucla</MenuItem>
                      <MenuItem value="Transbaikal Standard Time">(UTC+09:00) Chita</MenuItem>
                      <MenuItem value="Tokyo Standard Time">(UTC+09:00) Osaka, Sapporo, Tokyo</MenuItem>
                      <MenuItem value="North Korea Standard Time">(UTC+09:00) Pyongyang</MenuItem>
                      <MenuItem value="Korea Standard Time">(UTC+09:00) Seoul</MenuItem>
                      <MenuItem value="Yakutsk Standard Time">(UTC+09:00) Yakutsk</MenuItem>
                      <MenuItem value="Cen. Australia Standard Time">(UTC+09:30) Adelaide</MenuItem>
                      <MenuItem value="AUS Central Standard Time">(UTC+09:30) Darwin</MenuItem>
                      <MenuItem value="E. Australia Standard Time">(UTC+10:00) Brisbane</MenuItem>
                      <MenuItem value="AUS Eastern Standard Time">(UTC+10:00) Canberra, Melbourne, Sydney</MenuItem>
                      <MenuItem value="West Pacific Standard Time">(UTC+10:00) Guam, Port Moresby</MenuItem>
                      <MenuItem value="Tasmania Standard Time">(UTC+10:00) Hobart</MenuItem>
                      <MenuItem value="Vladivostok Standard Time">(UTC+10:00) Vladivostok</MenuItem>
                      <MenuItem value="Lord Howe Standard Time">(UTC+10:30) Lord Howe Island</MenuItem>
                      <MenuItem value="Bougainville Standard Time">(UTC+11:00) Bougainville Island</MenuItem>
                      <MenuItem value="Russia Time Zone 10">(UTC+11:00) Chokurdakh</MenuItem>
                      <MenuItem value="Magadan Standard Time">(UTC+11:00) Magadan</MenuItem>
                      <MenuItem value="Norfolk Standard Time">(UTC+11:00) Norfolk Island</MenuItem>
                      <MenuItem value="Sakhalin Standard Time">(UTC+11:00) Sakhalin</MenuItem>
                      <MenuItem value="Central Pacific Standard Time">(UTC+11:00) Solomon Is., New Caledonia</MenuItem>
                      <MenuItem value="Russia Time Zone 11">(UTC+12:00) Anadyr, Petropavlovsk-Kamchatsky</MenuItem>
                      <MenuItem value="New Zealand Standard Time">(UTC+12:00) Auckland, Wellington</MenuItem>
                      <MenuItem value="UTC+12">(UTC+12:00) Coordinated Universal Time+12</MenuItem>
                      <MenuItem value="Fiji Standard Time">(UTC+12:00) Fiji</MenuItem>
                      <MenuItem value="Kamchatka Standard Time">(UTC+12:00) Petropavlovsk-Kamchatsky - Old</MenuItem>
                      <MenuItem value="Chatham Islands Standard Time">(UTC+12:45) Chatham Islands</MenuItem>
                      <MenuItem value="UTC+13">(UTC+13:00) Coordinated Universal Time+13</MenuItem>
                      <MenuItem value="Tonga Standard Time">(UTC+13:00) Nuku'alofa</MenuItem>
                      <MenuItem value="Samoa Standard Time">(UTC+13:00) Samoa</MenuItem>
                      <MenuItem value="Line Islands Standard Time">(UTC+14:00) Kiritimati Island</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={12}>
                  <TextField
                    label={t('companyView.address')}
                    value={formData.address || ''}
                    onChange={handleInputChange('address')}
                    fullWidth
                    multiline
                    rows={2}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    label={t('companyView.phone')}
                    value={formData.phone || ''}
                    onChange={handleInputChange('phone')}
                    fullWidth
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    label={t('companyView.email')}
                    type="email"
                    value={formData.email || ''}
                    onChange={handleInputChange('email')}
                    fullWidth
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    label={t('companyView.website')}
                    value={formData.website || ''}
                    onChange={handleInputChange('website')}
                    fullWidth
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label={t('companyView.contactName')}
                    value={formData.contactname || ''}
                    onChange={handleInputChange('contactname')}
                    fullWidth
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label={t('companyView.contactEmail')}
                    type="email"
                    value={formData.contactemail || ''}
                    onChange={handleInputChange('contactemail')}
                    fullWidth
                  />
                </Grid>
                <Grid size={12}>
                  <TextField
                    label={t('companyView.remark')}
                    value={formData.remark || ''}
                    onChange={handleInputChange('remark')}
                    fullWidth
                    multiline
                    rows={2}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Technical Configuration */}
        <Grid size={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('companyView.generalSettings')}
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label={t('companyView.clientCode')}
                    value={formData.prefixword || ''}
                    onChange={handleInputChange('prefixword')}
                    fullWidth
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label={t('companyView.maturityMethod')}
                    value={formData.maturitymethod || ''}
                    onChange={handleInputChange('maturitymethod')}
                    fullWidth
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label={t('companyView.defaultMaturityMethod')}
                    value={formData.defaultmaturitymethod || ''}
                    onChange={handleInputChange('defaultmaturitymethod')}
                    fullWidth
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label={t('companyView.reportPrefix')}
                    value={formData.reportprefix || ''}
                    onChange={handleInputChange('reportprefix')}
                    fullWidth
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label={t('companyView.defaultCalibSpecifiedTemp')}
                    type="number"
                    value={formData.defaultcalibrationspecifiedtemp || ''}
                    onChange={handleInputChange('defaultcalibrationspecifiedtemp')}
                    fullWidth
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label={t('companyView.defaultCalibInternalTemp')}
                    type="number"
                    value={formData.defaultcalibrationinternaltemp || ''}
                    onChange={handleInputChange('defaultcalibrationinternaltemp')}
                    fullWidth
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label={t('companyView.loggerCalibFrequency')}
                    type="number"
                    value={formData.loggercalibrationfrequency || ''}
                    onChange={handleInputChange('loggercalibrationfrequency')}
                    fullWidth
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label={t('companyView.productCalibFrequency')}
                    type="number"
                    value={formData.productcalibrationfrequency || ''}
                    onChange={handleInputChange('productcalibrationfrequency')}
                    fullWidth
                  />
                </Grid>                
                {/* <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="Report Number"
                    value={formData.reportno || ''}
                    onChange={handleInputChange('reportno')}
                    fullWidth
                  />
                </Grid> */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>{t('companyView.language')}</InputLabel>
                    <Select
                      value={formData.language || ''}
                      onChange={handleInputChange('language')}
                      label={t('companyView.language')}
                    >
                      <MenuItem value="en">{t('companyEdit.english')}</MenuItem>
                      <MenuItem value="zh">{t('companyEdit.chinese')}</MenuItem>
                      <MenuItem value="es">{t('companyEdit.spanish')}</MenuItem>
                      <MenuItem value="fr">{t('companyEdit.french')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Format Settings */}
        <Grid size={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('companyView.unitsOfMeasure')}
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>{t('companyView.dateFormat')}</InputLabel>
                    <Select
                      value={formData.dateformat || ''}
                      onChange={handleInputChange('dateformat')}
                      label={t('companyView.dateFormat')}
                    >
                      <MenuItem value="01">{t('companyEdit.dateFormatAU')}</MenuItem>
                      <MenuItem value="02">{t('companyEdit.dateFormatUS')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>{t('companyView.temperatureFormat')}</InputLabel>
                    <Select
                      value={formData.tempformat || ''}
                      onChange={handleInputChange('tempformat')}
                      label={t('companyView.temperatureFormat')}
                    >
                      <MenuItem value="01">{t('companyEdit.celsius')}</MenuItem>
                      <MenuItem value="02">{t('companyEdit.fahrenheit')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>{t('companyView.evaFormat')}</InputLabel>
                    <Select
                      value={formData.evaformat || ''}
                      onChange={handleInputChange('evaformat')}
                      label={t('companyView.evaFormat')}
                    >
                      <MenuItem value="01">{t('companyEdit.evaMetric')}</MenuItem>
                      <MenuItem value="02">{t('companyEdit.evaImperial')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>{t('companyView.strengthFormat')}</InputLabel>
                    <Select
                      value={formData.strengthformat || ''}
                      onChange={handleInputChange('strengthformat')}
                      label={t('companyView.strengthFormat')}
                    >
                      <MenuItem value="01">{t('companyEdit.strengthMpa')}</MenuItem>
                      <MenuItem value="02">{t('companyEdit.strengthPsi')}</MenuItem>
                      <MenuItem value="03">{t('companyEdit.strengthNmm')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>{t('companyView.densityFormat')}</InputLabel>
                    <Select
                      value={formData.densityformat || ''}
                      onChange={handleInputChange('densityformat')}
                      label={t('companyView.densityFormat')}
                    >
                      <MenuItem value="01">{t('companyEdit.densityKg')}</MenuItem>
                      <MenuItem value="02">{t('companyEdit.densityLbYd')}</MenuItem>
                      <MenuItem value="03">{t('companyEdit.densityLbFt')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>{t('companyView.windSpeedFormat')}</InputLabel>
                    <Select
                      value={formData.windspeedformat || ''}
                      onChange={handleInputChange('windspeedformat')}
                      label={t('companyView.windSpeedFormat')}
                    >
                      <MenuItem value="01">{t('companyEdit.windKmh')}</MenuItem>
                      <MenuItem value="02">{t('companyEdit.windMph')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Success Snackbar */}
      <Snackbar
        open={success}
        autoHideDuration={3000}
        onClose={() => setSuccess(false)}
      >
        <Alert severity="success" onClose={() => setSuccess(false)}>
          {isNew
            ? t('companyEdit.createdSuccess', 'Company created')
            : t('companyEdit.updatedSuccess')}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CompanyEdit;
